import { ExternalLinkIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  Heading,
  HStack,
  Link,
  Stack,
  TabPanel,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";

import { JARCHIVE_LINK, SOURCE_CODE_LINK } from "../../misc/ui-constants";

type CreditRowProps = {
  label: string;
  children: React.ReactNode;
  isLast?: boolean;
};

function CreditRow({ label, children, isLast }: CreditRowProps) {
  const labelColor = useColorModeValue("gray.600", "gray.300");
  const dividerColor = useColorModeValue("gray.200", "whiteAlpha.200");

  return (
    <Box py={2}>
      <Flex
        gap={{ base: 1, md: 5 }}
        direction={{ base: "column", md: "row" }}
        align={{ base: "flex-start", md: "baseline" }}
      >
        <Text
          fontSize="sm"
          fontWeight="semibold"
          color={labelColor}
          minW={{ md: "200px" }}
        >
          {label}
        </Text>

        <Box flex="1">{children}</Box>
      </Flex>

      {!isLast && (
        <Box mt={2} borderBottomWidth="1px" borderColor={dividerColor} />
      )}
    </Box>
  );
}

export default function MenuPanel_Credits() {
  const cardBg = useColorModeValue("white", "gray.900");
  const cardBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const muted = useColorModeValue("gray.600", "gray.300");

  return (
    <TabPanel px={{ base: 4, md: 6 }} py={3}>
      <Flex justify="center">
        <Box w="full" maxW="2xl">
          <Stack spacing={3}>
            <Stack spacing={1} align="center" textAlign="center">
              <Heading size="md" letterSpacing="tight">
                Credits
              </Heading>

              <Link
                href={SOURCE_CODE_LINK}
                isExternal
                fontSize="sm"
                fontWeight="semibold"
                color={muted}
                _hover={{ color: "inherit", textDecoration: "none" }}
              >
                <HStack spacing={1}>
                  <Text as="u">Source code</Text>
                  <ExternalLinkIcon />
                </HStack>
              </Link>
            </Stack>

            <Box
              bg={cardBg}
              borderWidth="1px"
              borderColor={cardBorder}
              borderRadius="xl"
              boxShadow="sm"
              px={4}
              py={3}
            >
              <CreditRow label="Programming and UI">
                <Text>Isaac Redlon</Text>
              </CreditRow>

              <CreditRow label="Additional Programming">
                <Text>Theodore Redlon</Text>
              </CreditRow>

              <CreditRow label="Trivia Clues">
                <Link
                  href={JARCHIVE_LINK}
                  isExternal
                  color={muted}
                  _hover={{ color: "inherit", textDecoration: "none" }}
                >
                  <HStack justify="center">
                    <Text as="u">J! Archive</Text>
                    <ExternalLinkIcon />
                  </HStack>
                </Link>
              </CreditRow>

              <CreditRow label="Clue Decisions">
                <Text>OpenAI gpt-5.4-nano</Text>
              </CreditRow>

              <CreditRow label="Host Voices">
                <Text>OpenAI tts-1</Text>
              </CreditRow>

              <CreditRow label="Music">
                <Text>
                  Jeopardy Theme Song Remix - Attic Stein
                </Text>

                <Text>
                  Music by Alex Morgan from Pixabay
                </Text>
              </CreditRow>

              <CreditRow label="Fonts" isLast>
                <Text>
                  Bringbold Nineties (Zarma Type)
                  <br />
                  Alte Haas Grotesk (Yann Le Coroller)
                  <br />
                  OPTIKorinna-Agency (Castcraft Software)
                </Text>
              </CreditRow>
            </Box>

            <Box
              bg={useColorModeValue("gray.50", "whiteAlpha.50")}
              borderRadius="xl"
              p={3}
              borderWidth="1px"
              borderColor={useColorModeValue("gray.200", "whiteAlpha.200")}
            >
              <Text fontSize="sm" color={muted}>
                    <i>
                      jparty! is fan-made. It is not affiliated with, endorsed by, or sponsored by Jeopardy Productions, Inc. or Sony
                      Pictures Entertainment. <i>Jeopardy!</i> is a registered trademark of Jeopardy Productions, Inc.
                    </i>
                  </Text>
            </Box>
          </Stack>
        </Box>
      </Flex>
    </TabPanel>
  );
}
